import {
    IFrameRequest,
    IFrameRequestType,
    IFrameParentMessage,
    IFrameParentMessageType,
    IResponsePayload,
} from './iframe.types';
import { BioLibGlobalEvent } from '../../globalEvents';
import { IJobProgress, IRunJobPayload, runJob } from './runJob';
import { ModuleOutputV2 } from '@biolibtech/biolib-js';
import { downloadFile, getJobInputFileData, isFilePathGzippedText } from '@features/FileRenderer/utils';
import { queryIndex } from '../../apiHooks/indexApi';
import { utf8ToBase64 } from '../../apiHooks/utils';
import { getFileDataFromDataRecord, IGetFileFromDataRecordPayload } from './dataRecordUtils';
import { authApi } from '../../apiHooks/authApi';
import { store } from '../../state';
import {
    createExperiment,
    getExperiment,
    listExperiments,
    listExperimentResults,
} from './api/experiment';
import {
    createResultNote,
    getResult,
    getResultOutputFileData,
    getResultOutputFileInfo,
    listResultNotes,
    streamResultLogs,
} from './api/job';
import { getIndex } from './api/index';

interface IOptions {
    allowedRemoteHosts: string[];
    linkableRemoteHosts: string[];
    allowOpenUrlForAnyRemoteHost?: boolean;
    iframeId: string;
    overrideFileData?: Record<string, Uint8Array>;
    presignedStorageUrl?: string;
    viewRaw?: boolean;
    openSameOriginLinksInSameTab?: boolean;
    job?: {
        uuid: string;
        auth_token: string;
        app_uri: string;
        can_access_all_results_of_user: boolean;
        can_access_all_index_tables_of_user: boolean;
        can_access_all_data_records_of_user: boolean;
        can_access_oauth_token_of_user: boolean;
    };
    onLoadCallback?: (utils: { callUtilFunction: (functionName: string, args: any[]) => Promise<any> }) => void;
}

const processIframeMessage = (options: IOptions, liveEndpointAuthTokens: Map<string, string>, event: MessageEvent) => {
    const { iframeId, presignedStorageUrl, overrideFileData, onLoadCallback } = options;

    const callUtilFunction = (functionName: string, args: any[]): Promise<any> => {
        return new Promise((resolve, reject) => {
            const requestId = crypto.randomUUID();

            const handleUtilResponse = (event: MessageEvent) => {
                if (
                    event.origin === 'null' &&
                    event.data?.type === IFrameParentMessageType.utilFunctionResponse &&
                    event.data?.requestId === requestId
                ) {
                    window.removeEventListener('message', handleUtilResponse);
                    if (event.data.error) {
                        reject(new Error(event.data.error));
                    } else {
                        resolve(event.data.result);
                    }
                }
            };

            window.addEventListener('message', handleUtilResponse);

            const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    {
                        type: IFrameParentMessageType.utilFunctionInvocation,
                        requestId,
                        utilFunctionName: functionName,
                        args,
                    },
                    '*',
                );
            } else {
                window.removeEventListener('message', handleUtilResponse);
                reject(new Error('Failed to access iframe window'));
            }
        });
    };

    if (
        // Note: Since we importantly do not set sandbox='allow-same-origin' the iframe's origin will be null
        event.origin === 'null' &&
        event.data?.iframeId === iframeId &&
        typeof event.data?.type === 'string' &&
        typeof event.data?.requestId === 'string' &&
        typeof event.data?.payload === 'object' &&
        Object.values(IFrameRequestType).includes(event.data.type)
    ) {
        const { type, requestId, payload: requestPayload } = event.data as IFrameRequest;

        const sendResponse = (responsePayload: IResponsePayload) => {
            const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
            if (!iframe?.contentWindow) {
                console.warn(`Cannot send response for request ${requestId}: iframe ${iframeId} no longer exists`);
                return;
            }
            iframe.contentWindow.postMessage(
                {
                    requestId,
                    payload: responsePayload,
                    type: IFrameParentMessageType.generic,
                } as IFrameParentMessage,
                '*',
            );
        };

        switch (type) {
            case IFrameRequestType.openUrl: {
                const payload = requestPayload as { url: string };
                try {
                    const url = new URL(payload.url);
                    if (url.origin == 'file://') {
                        if (!presignedStorageUrl) {
                            console.warn('Cannot download file: presignedStorageUrl not available');
                        } else {
                            downloadFile(presignedStorageUrl, url.pathname);
                        }
                    } else if (
                        [...options.linkableRemoteHosts, location.hostname].includes(
                            url.hostname,
                        )
                    ) {
                        if (options.openSameOriginLinksInSameTab && url.origin === location.origin) {
                            window.location.href = url.toString();
                        } else {
                            window.open(url.toString(), '_blank', 'noopener,noreferrer');
                        }
                    } else if (options.allowOpenUrlForAnyRemoteHost && url.protocol === 'https:') {
                        if (options.job) {
                            console.error('allowOpenUrlForAnyRemoteHost should not be used when job is set');
                        } else {
                            window.open(url.toString(), '_blank', 'noopener,noreferrer');
                        }
                    } else {
                        console.warn(`Blocked opening URL: ${payload.url}`);
                    }
                } catch (error) {
                    console.error(`Failed to open URL: ${payload.url} due to: `, error);
                }
                sendResponse({ result: undefined });
                break;
            }

            case IFrameRequestType.getOutputFileData: {
                const { path } = requestPayload as { path: string };
                if (overrideFileData && path in overrideFileData) {
                    sendResponse({ result: overrideFileData[path] });
                } else if (!presignedStorageUrl) {
                    sendResponse({ error: 'presignedStorageUrl not available' });
                } else {
                    const parseGz = options.viewRaw && isFilePathGzippedText(path);
                    fetch(
                        `${location.origin}/proxy/files/v1/data/`
                            .concat(`?upstream_url_base64=${utf8ToBase64(presignedStorageUrl)}`)
                            .concat(`&path_base64=${utf8ToBase64(path)}`)
                            .concat(parseGz ? '&parse_gz=true' : ''),
                    )
                        .then((response) => {
                            if (response.status === 200) {
                                return response.arrayBuffer();
                            }
                            throw Error(`Response status ${response.status}`);
                        })
                        .then((arrayBuffer) => sendResponse({ result: new Uint8Array(arrayBuffer) }))
                        .catch((error) => sendResponse({ error: `Failed to get file data: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getInputFileData: {
                const { path } = requestPayload as { path: string };
                if (!options.job) {
                    sendResponse({ error: 'Input files not available' });
                } else {
                    const parseGz = options.viewRaw && isFilePathGzippedText(path);
                    getJobInputFileData(options.job, path, parseGz)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `Failed to get input file data: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.runJob: {
                const jobProgressHandler = (jobProgress: IJobProgress): void => {
                    const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
                    if (!iframe?.contentWindow) {
                        console.warn(`Cannot send job progress for request ${requestId}: iframe ${iframeId} no longer exists`);
                        return;
                    }
                    iframe.contentWindow.postMessage(
                        {
                            requestId,
                            payload: jobProgress,
                            type: IFrameParentMessageType.jobProgress,
                        } as IFrameParentMessage,
                        '*',
                    );
                };
                runJob(requestPayload as IRunJobPayload, jobProgressHandler)
                    .then((result) => {
                        if ('portEndpointUrls' in result && result.portEndpointUrls) {
                            for (const endpointUrl of result.portEndpointUrls) {
                                liveEndpointAuthTokens.set(new URL(endpointUrl).origin, result.authToken);
                            }
                        }
                        sendResponse({ result });
                    })
                    .catch((error) => sendResponse({ error: `Failed to run job: ${error}` }));
                break;
            }

            case IFrameRequestType.listOutputFilePaths: {
                if (!presignedStorageUrl) {
                    sendResponse({ error: 'presignedStorageUrl not available' });
                } else {
                    const url = new URL(presignedStorageUrl);
                    if (url.pathname.endsWith('module-output.bbf')) {
                        new ModuleOutputV2(presignedStorageUrl)
                            .getFiles()
                            .then((files) => {
                                const filePaths = files.map((file) => file.path);
                                sendResponse({ result: filePaths });
                            })
                            .catch((error) => sendResponse({ error: `Failed to get file paths: ${error}` }));
                    } else {
                        sendResponse({ error: 'Unimplemented' });
                    }
                }
                break;
            }

            case IFrameRequestType.getResult: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    getResult(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `getResult failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getResultOutputFileInfo: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    getResultOutputFileInfo(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `getResultOutputFileInfo failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getResultOutputFileData: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    getResultOutputFileData(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `getResultOutputFileData failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.streamResultLogs: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    streamResultLogs(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `streamResultLogs failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.createResultNote: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    createResultNote(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `createResultNote failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.listResultNotes: {
                const { resultId } = requestPayload as { resultId?: string };
                const isCurrentJob = !!options.job && resultId === options.job.uuid;
                if (!options.job?.can_access_all_results_of_user && !isCurrentJob) {
                    sendResponse({ error: 'Permission denied: Cannot access results' });
                } else {
                    listResultNotes(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `listResultNotes failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.createExperiment: {
                if (!options.job?.can_access_all_results_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access experiments' });
                } else {
                    createExperiment(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `createExperiment failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getExperiment: {
                if (!options.job?.can_access_all_results_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access experiments' });
                } else {
                    getExperiment(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `getExperiment failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.listExperiments: {
                if (!options.job?.can_access_all_results_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access experiments' });
                } else {
                    listExperiments(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `listExperiments failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.listExperimentResults: {
                if (!options.job?.can_access_all_results_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access experiments' });
                } else {
                    listExperimentResults(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `listExperimentResults failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getIndex: {
                if (!options.job?.can_access_all_index_tables_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access index tables' });
                } else {
                    getIndex(requestPayload as Record<string, unknown>)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `getIndex failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.queryIndex: {
                if (!options.job?.can_access_all_index_tables_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access index tables' });
                } else {
                    queryIndex(requestPayload as Parameters<typeof queryIndex>[0])
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `queryIndex failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.getOAuthAccessToken: {
                if (!options.job?.can_access_oauth_token_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access OAuth tokens' });
                } else {
                    const { scope } = requestPayload as { scope: string };
                    // @ts-ignore
                    store.dispatch(authApi.endpoints.oauthTokenExchange.initiate({ scope, job_uuid: options.job.uuid }))
                        .unwrap()
                        .then((data: { access_token: string }) => sendResponse({ result: data.access_token }))
                        .catch((error: { data?: { detail?: string } }) => sendResponse({ error: `getOAuthAccessToken failed: ${error.data?.detail || JSON.stringify(error)}` }));
                }
                break;
            }

            case IFrameRequestType.resizeIframe: {
                const { height } = event.data.payload as { height: number; width: number };
                const iframe = document.getElementById(iframeId);
                if (iframe) {
                    iframe.style.height = height + 'px';
                    console.debug(`Resized iframe ${iframeId} to height ${height}px`);
                }
                break;
            }

            case IFrameRequestType.iframeReady: {
                if (onLoadCallback) {
                    onLoadCallback({ callUtilFunction });
                }
                sendResponse({ result: undefined });
                break;
            }

            case IFrameRequestType.fetch: {
                try {
                    const payload = requestPayload as { url: string; method: string; body: any; headers: any };
                    const allowedMethods = ['HEAD', 'GET', 'POST', 'PATCH', 'PUT', 'DELETE', 'OPTIONS'];
                    const url = new URL(payload.url);
                    const liveEndpointAuthToken = liveEndpointAuthTokens.get(url.origin);
                    if (!options.allowedRemoteHosts.includes(url.host) && liveEndpointAuthToken === undefined) {
                        sendResponse({ error: `Remote hostname "${url.host}" not allowed.` });
                    } else if (!allowedMethods.includes(payload.method)) {
                        sendResponse({ error: `Method "${payload.method}" not allowed.` });
                    } else {
                        const headers = new Headers(payload.headers);
                        if (liveEndpointAuthToken !== undefined) {
                            headers.set('Authorization', `Token ${liveEndpointAuthToken}`);
                        }
                        fetch(payload.url, {
                            body: payload.body,
                            headers,
                            cache: 'no-cache',
                            credentials: 'omit',
                            keepalive: false,
                            method: payload.method,
                            mode: 'cors',
                            redirect: 'error',
                            referrer: '', // An empty string to omit the referrer header
                            signal: AbortSignal.timeout(30000),
                        })
                            .then((response) => {
                                response
                                    .arrayBuffer()
                                    .then((data) => {
                                        sendResponse({
                                            result: {
                                                body: new Uint8Array(data),
                                                status: response.status,
                                            },
                                        });
                                    })
                                    .catch((error) => sendResponse({ error: `Failed to get response body: ${error}` }));
                            })
                            .catch((error) => sendResponse({ error: `Failed to fetch: ${error}` }));
                    }
                } catch (error) {
                    sendResponse({ error: `Processing fetch request failed: ${error}` });
                }
                break;
            }

            case IFrameRequestType.getFileDataFromDataRecord: {
                if (!options.job?.can_access_all_data_records_of_user) {
                    sendResponse({ error: 'Permission denied: Cannot access data records' });
                } else {
                    getFileDataFromDataRecord(requestPayload as IGetFileFromDataRecordPayload)
                        .then((result) => sendResponse({ result }))
                        .catch((error) => sendResponse({ error: `Processing request failed: ${error}` }));
                }
                break;
            }

            case IFrameRequestType.keyboardShortcut: {
                const { shortcut } = requestPayload as { shortcut: string };
                if (shortcut === 'openSearch') {
                    window.dispatchEvent(new CustomEvent(BioLibGlobalEvent.openSearch));
                }
                sendResponse({ result: undefined });
                break;
            }

            default: {
                sendResponse({ error: 'Unrecognized request type' });
                break;
            }
        }
    } else {
        console.debug('Received unrecognized window event: ', event);
    }
};

export const getIframeMessageProcessor = (options: IOptions) => {
    const liveEndpointAuthTokens = new Map<string, string>();
    return (event: MessageEvent) => processIframeMessage(options, liveEndpointAuthTokens, event);
};
